import { useUser } from '@clerk/react';
import { useEffect } from 'react';
import { getAttribution } from '../../services/analytics/attribution';
import { useAnalytics } from './use-analytics';

/**
 * Freshly-created account = completed registration. Robust across the invite
 * flows (signup-link claim, admin invite) and direct organic sign-up because
 * every path creates the Clerk user at registration time, within seconds of
 * the conversion. The per-user marker makes it fire once even when the first
 * session resumes later or on a second tab.
 */
const SIGNUP_DETECTION_WINDOW_MS = 48 * 60 * 60 * 1000;

function trackingKey(userId: string): string {
  return `prospero_signup_tracked:${userId}`;
}

export function useSignupTracking(): void {
  const { isLoaded, isSignedIn, user } = useUser();
  const { trackEvent, setIdentityEmail } = useAnalytics();

  // Hashed email for Enhanced Conversions / Advanced Matching on conversions.
  const primaryEmail = user?.primaryEmailAddress?.emailAddress ?? null;
  useEffect(() => {
    if (isSignedIn && primaryEmail) {
      setIdentityEmail(primaryEmail);
    }
  }, [isSignedIn, primaryEmail, setIdentityEmail]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) {
      return;
    }
    const createdAt = user.createdAt instanceof Date ? user.createdAt.getTime() : Number.NaN;
    if (!Number.isFinite(createdAt) || Date.now() - createdAt > SIGNUP_DETECTION_WINDOW_MS) {
      return;
    }

    let alreadyTracked = false;
    try {
      const key = trackingKey(user.id);
      alreadyTracked = localStorage.getItem(key) === '1';
      if (!alreadyTracked) {
        localStorage.setItem(key, '1');
      }
    } catch {
      // Storage unavailable — without dedup state, skip to avoid double counts.
      return;
    }
    if (alreadyTracked) {
      return;
    }

    trackEvent('sign_up', { ...getAttribution() });
  }, [isLoaded, isSignedIn, user, trackEvent]);
}
